import { Card,Row,Col,Button, Flex, Popover, Radio, Table, Tag, Typography,Avatar } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { userRoles } from "../../../../../../constants";
import Actions from "../../../../../../redux/actions";
import {
  dateObjToDispViewDate,
  downloadUrl,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";

import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../../constants/colors";
import { PaperClipOutlined } from "@ant-design/icons";
import { getServiceProviderAppointmentsAction } from "../../../../../../redux/services/service-provider-appointments/serviceProviderAppointmentsServices";
import useDebounce from "../../../../../../hooks/useDebounce";
import DocumentsDialog from "./DocumentsDialog";
import dayjs from "dayjs";
import {
  baseRouteMaker,
  routes,
} from "../../../../../../constants/routeConsts";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import CompleteOrderDialog from "./CompleteOrderDialog";
import Spinner from "../../../../../common/spinner/Spinner";
import { downloadCustomerAppointmentPdfAction } from "../../../../../../redux/services/common/commonServices";
import { CalendarOutlined,UserOutlined } from "@ant-design/icons";

const AppointmentsInfo = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { serviceProviderAppointmentsInfo } = useSelector(
    (state) => state.serviceProviderAppointments
  );
  const [searchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviderAppointmentsInfo?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openDocumentsPopOver, setOpenDocumentsPopOver] = useState([]);
  const [openOrderCompletionDialog, setOpenOrderCompletionDialog] =
    useState(false);
  const [orderStatus, setOrderStatus] = useState(
    `ASSIGNED_TO_SERVICE_PROVIDER APPROVED_BY_SERVICE_PROVIDER`
  );
  const [serviceProviderStatus, setServiceProviderStatus] =
    useState("APPROVED");
  /* dialog dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [selectedCountryObj] = useState(null);
  /* filter dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_SERVICE_PROVIDER_APPOINTMENTS_LIST, {
        serviceProviderAppointmentsInfo: {},
      })
    );
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selectedCountryObj,
      service_status: serviceProviderStatus,
      staff:
        userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN
          ? ""
          : userSession?.userId,
      staffType:
        userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN
          ? ""
          : userSession?.userRole,
    };
    dispatch(getServiceProviderAppointmentsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchValue, orderStatus, serviceProviderStatus]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Appointments.orderNumber"),
        dataIndex: "order_number",
        key: "order_number",
      },
      {
        title: t("Modules.Appointments.orderType"),
        dataIndex: "order_type",
        key: "order_type",
      },
      {
        title: t("Modules.customer.customerName"),
        render: (_,record) => {
          return (
            record?.user ? (<p>{record?.user?.first_name} {record?.user?.last_name}</p>):''
          );
        },
      },
      {
        title: t("appointmentInfo.bookingFor"),
        dataIndex: "booking_for",
        key: "booking_for",
        render: (_, record) => (
          <p>{record?.booking_for ? record?.booking_for : "-"}</p>
        ),
      },
      {
        title: t("appointmentInfo.patientName"),
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <p>
            {record?.booking_for_details
              ? `${record?.booking_for_details?.first_name} ${record?.booking_for_details?.last_name}`
              : "-"}
          </p>
        ),
      },

      {
        title: t("appointmentInfo.service"),
        dataIndex: "service",
        key: "service",
        render: (_, record) => (
          <p>{record?.service_name ? record?.service_name : "-"}</p>
        ),
      },
      {
        title: t("appointmentInfo.appointmentDate"),
        dataIndex: "appointment_date",
        key: "appointment_date",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.appointment_date, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("appointmentInfo.appointmentTime"),
        dataIndex: "appointment_date_time",
        key: "appointment_date_time",
        render: (_, record) => (
          <p>
            {timeObjToDispViewTimeWithSuffix(dayjs(record?.appointment_date_time).utc().local().format('ddd, MMM D, H:mm z'))}
          </p>
        ),
      },
      {
        title: t("appointmentInfo.appointmentMode"),
        dataIndex: "appointment_mode",
        key: "appointment_mode",
      },
      {
        title: t("appointmentInfo.staff"),
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <p>
            {Object.keys(record?.service_provider)?.length > 0
              ? `${record?.service_provider?.first_name} ${record?.service_provider?.last_name}`
              : "-"}
          </p>
        ),
      },
      {
        title: t("appointmentInfo.paymentStatus"),
        key: "is_payment_completed",
        dataIndex: "is_payment_completed",
        render: (_, { is_payment_completed }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background: is_payment_completed
                  ? colors.lightTeal
                  : colors.lightPinkishRed,
                color: is_payment_completed
                  ? colors.darkTeal
                  : colors.darkMaroon,
              }}
            >
              {is_payment_completed ? t("Modules.Appointments.paid") : t("Modules.Appointments.due")}
            </Tag>
          </>
        ),
      },
      {
        title: t("appointmentInfo.nationalId"),
        key: "national_id",
        dataIndex: "national_id",
        render: (_, record, index) => {
          return (
            <Flex gap="1rem" align="center">
              <Popover
                content={
                  <DocumentsDialog
                    orderId={record._id}
                    onClose={(orderId) => {
                      const tempOpen = [...openDocumentsPopOver];
                      tempOpen[orderId] = false;
                      setOpenDocumentsPopOver(tempOpen);
                      handleGetTableData();
                    }}
                  />
                }
                trigger="click"
                open={openDocumentsPopOver[record._id]}
                onOpenChange={(newOpen) => {
                  const tempOpen = [...openDocumentsPopOver];
                  tempOpen[record._id] = newOpen;
                  setOpenDocumentsPopOver(tempOpen);
                  !newOpen && handleGetTableData();
                }}
              >
                <Button
                  type="primary"
                  shape="circle"
                  size="middle"
                  icon={<PaperClipOutlined style={{ fontSize: 22 }} />}
                />
              </Popover>
            </Flex>
          );
        },
      },
      {
        title: t("appointmentInfo.serviceInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
      {
        title: t("CommonUtils.price"),
        dataIndex: "service_price",
        key: "service_price",
        render: (_, record) => <>{`${record?.service_price?.toFixed(2)} ${record?.currency}`}</>,
      },
      {
        title: t("CommonUtils.subtotal"),
        dataIndex: "subtotal_amount",
        key: "subtotal_amount",
        render: (_, record) => <>{`${record?.subtotal_amount?.toFixed(2)} ${record?.currency}`}</>,
      },
      {
        title: t("CommonUtils.tax"),
        dataIndex: "tax_amount",
        key: "tax_amount",
        render: (_, record) => <>{`${record?.tax_amount?.toFixed(2)} ${record?.currency}`}</>,
      },
      {
        title: t("CommonUtils.total"),
        dataIndex: "total_amount",
        key: "total_amount",
        render: (_, record) => <>{`${record?.total_amount?.toFixed(2)} ${record?.currency}`}</>,
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        dataIndex: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {record?.order_type === "package" &&
              record?.service_status === "APPROVED" ? (
                <Button
                  type="primary"
                  size="middle"
                  onClick={(e) => handleCompleteOrder(e, record)}
                >
                  {t("appointmentInfo.completeOrder")}
                </Button>
              ) : record?.order_type !== "package" &&
                record?.service_provider_status === "APPROVED" ? (
                <Button
                  type="primary"
                  size="middle"
                  onClick={(e) => handleCompleteOrder(e, record)}
                >
                  {t("appointmentInfo.completeOrder")}
                </Button>
              ) : (
                <>-</>
              )}
            </>
          );
        },
      },
      {
        title: t("viewPdf"),
        key: "viewPdf",
        dataIndex: "viewPdf",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {
                <Typography.Link
                  onClick={(e) => handleViewPdfDetails(e, record)}
                >
                  {t("view")}
                </Typography.Link>
              }
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.order_status"),
        key: "system_status",
        dataIndex: "system_status",
        align: "center",
        render: (_, record) => (
          <Tag
            key={record?._id}
            style={{
              background:
                record?.service_status === "COMPLETED"
                  ? colors.darkSearGreen
                  : record?.service_status === "REJECTED"
                  ? colors.lightPinkishRed
                  : colors.slatePurple,
              color:
                record?.service_status === "COMPLETED"
                  ? colors.limeGreen
                  : record?.service_status === "REJECTED"
                  ? colors.darkMaroon
                  : colors.indigo,
            }}
            bordered={false}
          >
            {record?.service_status.replace("_", " ")}
          </Tag>
        ),
      }
    ];

    /* table column config */
    setColumns(() => {
      if (userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN) {
        if (serviceProviderStatus === "APPROVED") {
          return tempTableColumns?.filter(
            (element) => ![ "viewPdf"].includes(element?.dataIndex)
          );
        } else if (serviceProviderStatus === "COMPLETED") {
          return tempTableColumns?.filter(
            (element) => !["action"].includes(element?.dataIndex)
          );
        } else {
          return tempTableColumns?.filter(
            (element) =>
              !["action", "national_id", "viewPdf"].includes(element?.dataIndex)
          );
        }
      } else {
        return tempTableColumns?.filter(
          (element) =>
            !["national_id", "total_amount", "viewPdf"].includes(
              element?.dataIndex
            )
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceProviderStatus]);
  /* initial dependencies rendering */

  const handleViewPdfDetails = (e, row) => {
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      if (
        row?.invoice_url &&
        row?.invoice_url !== "" &&
        row?.invoice_url !== null &&
        row?.invoice_url !== undefined
      ) {
        downloadUrl(row?.invoice_url);
      } else {
        const selectedAppointmentsInfoId = row?._id;
        const dataToBeSent = {
          _id: selectedAppointmentsInfoId,
        };
        dispatch(
          downloadCustomerAppointmentPdfAction(
            dataToBeSent,
            setLoading,
            downloadUrl
          )
        );
      }
    }
  };

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_APPOINTMENTS_INFO_ID, {
        selectedAppointmentsInfoId: "",
      })
    );
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_ID_FOR_PACKAGE, {
        selectedServiceIdForPackage: "",
      })
    );
    navigate(baseRouteMaker(routes.SERVICE_INFO_LIST));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedAppointmentsInfoId = row?._id;
      const selectedServiceIdForPackage = row?.service_id;
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENTS_INFO_ID, {
          selectedAppointmentsInfoId,
        })
      );
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_ID_FOR_PACKAGE, {
          selectedServiceIdForPackage,
        })
      );
    }
  };

  /* function definition for order completion */
  const handleCompleteOrder = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_APPOINTMENTS_INFO_ID, {
        selectedAppointmentsInfoId: "",
      })
    );
    dispatch(
      Actions.createAction(Actions.SELECTED_APPOINTMENT_ORDER_TYPE, {
        selectedAppointmentOrderType: "",
      })
    );
    setOpenOrderCompletionDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedAppointmentsInfoId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENTS_INFO_ID, {
          selectedAppointmentsInfoId,
        })
      );
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_ORDER_TYPE, {
          selectedAppointmentOrderType: row?.order_type,
        })
      );
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_PACKAGE_SERVICE_ID, {
          selectedAppointmentPackageServiceId: row?.service_id,
        })
      );
    }
  };
  /* function definition for order completion */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex justify="space-between">
        <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<CalendarOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                    {t("appointmentInfo.appointments")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${serviceProviderAppointmentsInfo?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          <Radio.Group
            value={serviceProviderStatus}
            onChange={(event) => setServiceProviderStatus(event.target.value)}
          >
            <Radio
              // value={`ASSIGNED_TO_SERVICE_PROVIDER APPROVED_BY_SERVICE_PROVIDER`}
              value={`APPROVED`}
            >
              {t("appointmentInfo.approved")}
            </Radio>
            <Radio value={"COMPLETED"}>{t("appointmentInfo.completed")}</Radio>
            <Radio value={"REJECTED"}>{t("appointmentInfo.rejected")}</Radio>
            <Radio
              // value={`ASSIGNED_TO_SERVICE_PROVIDER APPROVED_BY_SERVICE_PROVIDER`}
              value={`NOT_AVAILABLE`}
            >
              {t("appointmentInfo.noShow")}
            </Radio>
          </Radio.Group>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <div
            style={{
              width: "100%",
              maxWidth: "100vw - 20rem",
              overflowX: "auto",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Table
              dataSource={serviceProviderAppointmentsInfo?.data}
              columns={columns}
              pagination={paginationObj}
            />
          </div>
        )}
      </Flex>
      <CompleteOrderDialog
        open={openOrderCompletionDialog}
        onClose={() => {
          setOpenOrderCompletionDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default AppointmentsInfo;
